import { useParams, useSearchParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  ToastSeverity,
  projectState,
  selectedPatientState,
  toastStateSelector,
} from 'state';
import { GeneralIds, ModelTypes, SupportedIdentifiers } from 'types/types';
import { useEffect, useState } from 'react';
import PatientEditorForm from './PatientEditorForm';
import { getNextPatientId, getPatientByIdentifier } from 'api';
import { t } from 'i18next';
import { query } from 'operations';
import { Patient } from 'api/models';
import { CircularProgress } from '@mui/material';
import { Box } from '@mui/system';

interface PatientEditorProps {
  mode: 'view' | 'edit';
}

export default function PatientEditor({ mode = 'edit' }: PatientEditorProps) {
  const { patientId, projectId } = useParams<GeneralIds>();
  const { selectedProject } = useRecoilValue(projectState);
  const [automaticPatientId, setAutomaticPatientId] = useState<string>('');
  const [searchParams] = useSearchParams();
  const setToast = useSetRecoilState(toastStateSelector);
  const [patient, setPatient] = useRecoilState(selectedPatientState);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      // if patientId is provided in the URL, we are editing
      if (patientId) {
        const { data: patient } = await query<Patient>({
          type: ModelTypes.patient,
          ids: { id: patientId, projectId },
        });

        setPatient(patient);
      } else {
        setPatient(undefined);
        try {
          // if ID is not provided but idNumber is, we try to fetch by that to see if the patient already exists
          // if the patient does not exist this call fails with 404
          const exisitingPatient = await getPatientByIdentifier(projectId, {
            id: searchParams.get('idNumber') ?? '',
            type: (searchParams.get('idType') as SupportedIdentifiers) ?? '',
          });

          if (selectedProject?.configuration?.automaticProjectId) {
            const nextId = await getNextPatientId(projectId);
            setAutomaticPatientId(nextId);
          }

          setPatient(exisitingPatient);
          setToast({
            isOpen: true,
            message: t('toastMessages.patientAlreadyExists'),
            severity: ToastSeverity.warning,
          });
        } catch (_e) {
          if (selectedProject?.configuration?.automaticProjectId) {
            const nextId = await getNextPatientId(projectId);
            setAutomaticPatientId(nextId);
          }
        }
      }
      setIsLoading(false);
    };
    fetchData();
  }, [
    patientId,
    projectId,
    searchParams,
    selectedProject,
    setPatient,
    setToast,
  ]);

  return isLoading ? (
    <Box
      width={'100%'}
      minHeight={'300px'}
      display={'flex'}
      alignItems={'center'}
      justifyContent={'center'}
    >
      <CircularProgress />
    </Box>
  ) : (
    <PatientEditorForm
      mode={mode}
      id={patientId}
      patient={patient}
      patientProjectId={automaticPatientId}
    />
  );
}
